import React, { useCallback, useEffect, useState } from 'react';
import { BeakerIcon, ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/outline';
import nappiService from '../../services/nappiService';
import { errText } from '../../services/scheduleService';
import { rands } from '../../components/billing/TariffLines';

const TYPE = { medicine: 'Medicine', consumable: 'Consumable' };
const EMPTY = { nappi_code: '', description: '', item_type: 'medicine', strength: '', unit: 'each', unit_price: '', notes: '', is_active: true };
const input = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';

/**
 * NappiPanel (Codes & Tariffs) — the practice's medicines and consumables, by NAPPI
 * code, with the price per unit that goes on invoices. The demo rows are placeholders
 * (fake 99xxxxx codes) until the practice enters its own or a licensed NAPPI file is imported.
 */
const NappiPanel = () => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [form, setForm] = useState(null); // { ...item } being added or edited
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      setItems(await nappiService.list({ all: true, type }));
    } catch (e) {
      setErr(errText(e));
    }
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const shown = (items || []).filter((i) => {
    const t = q.trim().toLowerCase();
    return !t || i.nappi_code.startsWith(t) || `${i.description} ${i.strength || ''}`.toLowerCase().includes(t);
  });
  const demo = (items || []).filter((i) => i.source === 'placeholder' && i.is_active).length;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      if (form.item_id) await nappiService.update(form.item_id, form);
      else await nappiService.create(form);
      setForm(null);
      load();
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
        <BeakerIcon className="h-5 w-5 text-brand-teal" />
        <div className="flex-1">
          <h2 className="font-bold text-slate-800">Medicines &amp; consumables</h2>
          <p className="text-xs text-slate-500">NAPPI codes and the price per unit the practice bills. Prices are VAT-inclusive.</p>
        </div>
        <button onClick={() => { setForm({ ...EMPTY }); setErr(''); }} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-3 py-2 text-sm font-semibold text-white">
          <PlusIcon className="h-4 w-4" /> Add item
        </button>
      </div>

      {demo > 0 && (
        <div className="mx-5 mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{demo} demo item{demo === 1 ? ' is' : 's are'} still in use — fake NAPPI codes (99…) and made-up prices. Edit them with your real codes and prices, or deactivate them.</span>
        </div>
      )}

      {form && (
        <form onSubmit={save} className="mx-5 mt-4 grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 md:grid-cols-6">
          <label className="text-xs font-semibold text-slate-600">NAPPI code<input value={form.nappi_code} onChange={set('nappi_code')} className={`mt-1 font-mono ${input}`} inputMode="numeric" maxLength={10} /></label>
          <label className="col-span-2 text-xs font-semibold text-slate-600">Description<input value={form.description} onChange={set('description')} className={`mt-1 ${input}`} /></label>
          <label className="text-xs font-semibold text-slate-600">Strength / size<input value={form.strength || ''} onChange={set('strength')} className={`mt-1 ${input}`} placeholder="e.g. 200 mg/20 ml" /></label>
          <label className="text-xs font-semibold text-slate-600">Type
            <select value={form.item_type} onChange={set('item_type')} className={`mt-1 ${input}`}><option value="medicine">Medicine</option><option value="consumable">Consumable</option></select>
          </label>
          <label className="text-xs font-semibold text-slate-600">Billed per<input value={form.unit} onChange={set('unit')} className={`mt-1 ${input}`} placeholder="ampoule, vial, ml, each" /></label>
          <label className="text-xs font-semibold text-slate-600">Price per unit (R)<input type="number" min="0" step="0.01" value={form.unit_price} onChange={set('unit_price')} className={`mt-1 ${input}`} /></label>
          <label className="col-span-2 text-xs font-semibold text-slate-600 md:col-span-3">Notes<input value={form.notes || ''} onChange={set('notes')} className={`mt-1 ${input}`} /></label>
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-slate-600"><input type="checkbox" checked={form.is_active} onChange={set('is_active')} /> In use</label>
          <div className="col-span-2 flex items-end justify-end gap-2 md:col-span-1">
            <button type="button" onClick={() => setForm(null)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600">Cancel</button>
            <button disabled={busy} className="rounded-lg bg-brand-navy px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      )}
      {err && <p className="mx-5 mt-3 text-sm text-red-600">{err}</p>}

      <div className="flex flex-wrap gap-2 px-5 py-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or code" className="w-64 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
          <option value="">Medicines and consumables</option><option value="medicine">Medicines</option><option value="consumable">Consumables</option>
        </select>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50">
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-5 py-2 font-semibold">NAPPI</th><th className="px-2 py-2 font-semibold">Item</th><th className="px-2 py-2 font-semibold">Type</th>
              <th className="px-2 py-2 text-right font-semibold">Price</th><th className="px-5 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.map((i) => (
              <tr key={i.item_id} className={i.is_active ? '' : 'text-slate-400'}>
                <td className="px-5 py-2 font-mono font-semibold text-brand-navy">{i.nappi_code}</td>
                <td className="px-2 py-2">
                  {i.description}{i.strength ? <span className="text-slate-500"> · {i.strength}</span> : null}
                  {i.source === 'placeholder' && <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">DEMO</span>}
                  {!i.is_active && <span className="ml-2 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">NOT IN USE</span>}
                </td>
                <td className="px-2 py-2 text-slate-500">{TYPE[i.item_type]}</td>
                <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{rands(i.unit_price)} <span className="text-xs text-slate-400">/ {i.unit}</span></td>
                <td className="px-5 py-2 text-right"><button onClick={() => { setForm({ ...i, strength: i.strength || '', notes: i.notes || '' }); setErr(''); }} className="text-xs font-semibold text-brand-teal hover:underline">Edit</button></td>
              </tr>
            ))}
            {items && shown.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-center text-slate-400">Nothing here.</td></tr>}
            {!items && <tr><td colSpan={5} className="px-5 py-6 text-center text-slate-400">Loading…</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default NappiPanel;
